import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';


function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

function isCounterPurchaseOrder(po) {
  return String(po?.poId || '').endsWith('#counter');
}

export default function VendorPaymentReportPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [projectFilter, setProjectFilter] = useState('all');

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }

    loadProjects();
  }, [userId]);

  useEffect(() => {
    async function loadPurchaseOrders() {
      if (!userId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/all`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = (Array.isArray(data) ? data : []).filter((po) => !isCounterPurchaseOrder(po));
        const projectMap = projects.reduce((acc, p) => {
          acc[p.id] = p.name;
          return acc;
        }, {});

        setRows(
          list.map((po) => ({
            poId: po.poId || po.id || '',
            projectId: String(po.projectId || ''),
            projectName: projectMap[String(po.projectId)] || 'Untitled Project',
            vendorName: po.vendorName || '-',
            date: po.date || po.createdAt || po.updatedAt || '',
            orderNumber: po.orderNumber || po.poId || '-',
            status: po.paymentStatus || 'draft',
            total: Number(po.total || 0),
            paid: Number(po.payments || 0),
          }))
        );
      } catch (err) {
        console.error('Failed to load purchase orders', err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    loadPurchaseOrders();
  }, [projects, userId]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (projectFilter !== 'all' && r.projectId !== projectFilter) return false;
      const date = String(r.date || '').slice(0, 10);
      if (startDate && date && date < startDate) return false;
      if (endDate && date && date > endDate) return false;
      return true;
    });
  }, [rows, projectFilter, startDate, endDate]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.total += r.total;
        acc.paid += r.paid;
        return acc;
      },
      { total: 0, paid: 0 }
    );
  }, [filteredRows]);

  function statusClass(status) {
    return String(status || 'draft')
      .toLowerCase()
      .replace(/\\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function exportExcel() {
    const headers = ['Date', 'Project', 'PO', 'Vendor', 'Status', 'Total', 'Paid', 'Balance'];
    const rowsForExport = filteredRows.map((r) => [
      r.date ? String(r.date).slice(0, 10) : '',
      r.projectName,
      r.orderNumber,
      r.vendorName,
      r.status,
      Number(r.total || 0),
      Number(r.paid || 0),
      Math.max(Number(r.total || 0) - Number(r.paid || 0), 0),
    ]);

    exportRowsToExcel({
      filename: 'vendor-payment-report.xlsx',
      sheetName: 'Vendor Payments',
      headers,
      rows: rowsForExport,
    });
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Vendor Payment Report</h1>
            <div className="muted">
              {startDate} - {endDate}
            </div>
          </div>

          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <input
            className="report-input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            className="report-input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.total)}</div>
              <div className="report-metric-label">Total Purchase Orders</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.paid)}</div>
              <div className="report-metric-label">Total Paid</div>
            </div>
            <div className="report-metric">
              <div className="report-metric-value">
                {formatCurrency(Math.max(totals.total - totals.paid, 0))}
              </div>
              <div className="report-metric-label">Open Balance</div>
            </div>
          </div>
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading vendor payments...</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">No purchase orders found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Vendor payments">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>PO</th>
                  <th>Vendor</th>
                  <th>Status</th>
                  <th className="right">Total</th>
                  <th className="right">Paid</th>
                  <th className="right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={r.poId}>
                    <td>{formatDate(r.date)}</td>
                    <td>{r.projectName}</td>
                    <td>
                      {r.projectId && r.poId ? (
                        <a href={`/projects/${r.projectId}/purchase-orders/${r.poId}`}>
                          {r.orderNumber}
                        </a>
                      ) : (
                        r.orderNumber
                      )}
                    </td>
                    <td>{r.vendorName}</td>
                    <td>
                      <span className={`report-badge status-${statusClass(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="right">{formatCurrency(r.total)}</td>
                    <td className="right">{formatCurrency(r.paid)}</td>
                    <td className="right">{formatCurrency(Math.max(r.total - r.paid, 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
